import type { CheckboxTreeState } from '@lib/components/CheckboxTree/types'
import { getId } from '@store/utils'

import { prepareUrl } from '../../utils'
import type { Node } from './types'

export const buildTreeData = (urls: string[]): Node => {
  const rootNode: Node = { id: getId(), count: 0, prefix: '' }

  const appendUrl = (url: string, level = 0, node = rootNode) => {
    node.count++

    const parts = url.split(/(\/[^/]+)/).filter((part) => part.length > 1)
    if (level === parts.length) {
      if (node.children) {
        const newValue: Node = {
          id: getId(),
          count: 1,
          prefix: '/',
          value: url,
        }
        node.children.push(newValue)
      } else {
        node.value = url
      }
      return
    }

    const part = parts[level]

    const nextNode = node.children?.find((child) => child.prefix === part)
    if (nextNode) {
      appendUrl(url, level + 1, nextNode)
      return
    }

    const newValue: Node = { id: getId(), count: 0, prefix: part }
    node.children = [...(node.children || []), newValue]
    if (node.value) {
      node.children.unshift({
        id: getId(),
        count: 1,
        prefix: '/',
        value: node.value,
      })
      delete node.value
    }

    appendUrl(url, level + 1, newValue)
  }

  urls.forEach((url) => {
    appendUrl(url)
  })

  return rootNode
}

export const convertTreeToList = (
  root: Node,
  checked: boolean
): CheckboxTreeState => {
  const resultState: CheckboxTreeState = new Map()

  const addNode = (
    node: Node,
    prefix = '',
    parentId: string | null = null
  ): string => {
    if (node.children?.length === 1) {
      return addNode(node.children[0], prefix + node.prefix, parentId)
    }

    let label: string | undefined
    // The root's label is different from the rest
    // And exactly root gets into resultState first
    if (!Array.from(resultState.values()).length) {
      label = node.count > 1 ? `${node.count} pages` : undefined
    } else if (node.count > 1) {
      label = String(node.count)
    }
    resultState.set(node.id, {
      id: node.id,
      value: node.value,
      parentId,
      checked,
      indeterminate: false,
      prefix: prefix + node.prefix,
      label,
    })
    if (node.children && resultState.get(node.id)) {
      node.children.sort((a, b) => b.count - a.count)
      resultState.get(node.id)!.children = node.children?.flatMap((child) =>
        addNode(child, '', node.id)
      )
    }
    return node.id
  }

  addNode(root)

  return resultState
}

interface CrawlUrlParams {
  baseUrl: string
  website: string
  userId: string | null
  pro: boolean
  metaTag: boolean
  blocks: boolean
  blocksContent: boolean
  excludedUrls: string[]
}

// A signed-in user runs a background job (`/jobs/run`); a guest streams the
// crawl from the root. Flags are presence-only: the crawler checks `has()`.
export const buildCrawlUrl = ({
  baseUrl,
  website,
  userId,
  pro,
  metaTag,
  blocks,
  blocksContent,
  excludedUrls,
}: CrawlUrlParams): string => {
  const url = new URL(userId ? `${baseUrl}/jobs/run` : baseUrl)
  url.searchParams.set('url', website)

  if (userId) {
    url.searchParams.set('userId', userId)
  }
  if (pro) {
    url.searchParams.set('pro', 'true')
  }
  if (metaTag) {
    url.searchParams.set('meta', 'true')
  }
  if (blocks) {
    url.searchParams.set('blocks', 'true')
  }
  if (blocks && blocksContent) {
    url.searchParams.set('content', 'true')
  }
  excludedUrls.forEach((excludedUrl) => {
    url.searchParams.append('excludedUrls', excludedUrl)
  })

  return url.toString()
}

// Path exclusions entered for the original site still apply after confirming a
// new domain. Keep substring patterns and explicitly unrelated domains intact.
export const redirectExclusions = (
  exclusions: string[],
  source: string,
  destination: string
): string[] => {
  const sourceOrigin = new URL(prepareUrl(source)).origin
  const destinationOrigin = new URL(destination).origin

  return exclusions.map((exclusion) =>
    exclusion === sourceOrigin || exclusion.startsWith(`${sourceOrigin}/`)
      ? destinationOrigin + exclusion.slice(sourceOrigin.length)
      : exclusion
  )
}
